<template>
    <div class="container">
        <v-title :lineage="[{'Products':'products'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Pricing Plan for {{ productId | truncate({limit: 26, dots: true}) }}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages['products'].isAllowed"
                    class="btn inline-s" :to="{name: 'products'}">
                    Cancel
                </router-link>
                <r-button
                    type="primary" @click="save" :loading="isLoading">Add Pricing Plan
                </r-button>
            </div>
        </v-title>
        <v-inline-notifications type="info" class="stack-l" permanent>
            <p slot="content">You'll only be able to edit the plan's name once you submit this form. </p>
        </v-inline-notifications>

        <v-error-messages
            :has-frontend-errors="$v.plan.$error"
            :server-error="serverError"
            :predicates="validationPredicates"
            class="stack-l">
            <li class="list-item" v-if="!$v.plan.id.resourceId"><span>The plan ID cannot contain spaces or special characters other than <strong>@ ~ _ -</strong> and should be less than <strong>50</strong> characters long.</span></li>
            <li class="list-item" v-if="!$v.plan.name.required">Plan name is required</li>
            <template v-if="recurringInterval === 'limited'">
                <li class="list-item" v-if="!$v.plan.recurringInterval.limit.required">Plan recurring limit is
                    required
                </li>
                <li class="list-item" v-if="!$v.plan.recurringInterval.limit.decimal">Plan recurring limit must be a
                    number
                </li>
            </template>
            <template v-if="isRecurringPlan && plan.recurringInterval">
                <li class="list-item" v-if="!$v.plan.recurringInterval.length.required">Plan recurring period length is
                    required
                </li>
                <li class="list-item" v-if="!$v.plan.recurringInterval.length.decimal">Plan recurring period length must
                    be a number
                </li>
                <li class="list-item" v-if="!$v.plan.recurringInterval.unit.required">Plan recurring period unit is
                    required
                </li>
            </template>
            <template v-if="hasTrial">
                <li class="list-item" v-if="!$v.plan.trial.price.required">Plan trial price is required</li>
                <li class="list-item" v-if="!$v.plan.trial.price.decimal">Plan trial price must be a number</li>
                <li class="list-item" v-if="!$v.plan.trial.period.length.required">Plan trial period length is
                    required
                </li>
                <li class="list-item" v-if="!$v.plan.trial.period.length.decimal">Plan trial period length must be a
                    number
                </li>
                <li class="list-item" v-if="!$v.plan.trial.period.unit.required">Plan trial period unit is required</li>
            </template>
            <template v-if="hasSetup">
                <li class="list-item" v-if="!$v.plan.setup.price.required">Plan setup price is required</li>
                <li class="list-item" v-if="!$v.plan.setup.price.decimal">Plan setup price must be a number</li>
            </template>
            <template v-if="plan.pricing.isSimple">
                <li class="list-item" v-if="!$v.plan.pricing.price.required">Plan price is required</li>
                <li class="list-item" v-if="!$v.plan.pricing.price.decimal">Plan price must be a number</li>
            </template>
            <template v-if="plan.pricing.isBracket">
                <li class="list-item" v-if="$v.plan.pricing.brackets.$error">Please check your pricing brackets</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Plan Info</h2>
            <p slot="description" class="text-muted">A Pricing plan, or Plan, describes how a specific variation of your
                product should be billed. This includes quantity-based price breaks, recurring schedule (if any), and
                more.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                :disabled="isFormUpdating"
                                v-model="plan.id"
                                label="Plan ID"
                                help-text="A unique identifier for your plan, used to reference it."
                                :validate="$v.plan.id"
                                class="slug-target"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                v-model="plan.name"
                                label="Plan Name"
                                help-text="A name you give your plan, it may appear on receipts and invoices."
                                :validate="$v.plan.name"
                                auto-focus
                                class="slug-source"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Currency"
                                help-text="A plan is in a specific currency. You can create other plans with different currencies."
                                :preselect-first="true"
                                :options="currenciesList"
                                v-model="plan.currency"/>
                        </div>
                        <v-custom-fields-manager
                            resource-type="plans"
                            v-model="plan.customFields"/>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Pricing</h2>
             <p slot="description" class="text-muted stack-m">When charged, this will appear as a single line item on an
                invoice or receipt. How the amount is calculated is a function of the quantity and the formula, and the
                price or price brackets.</p>
            <p slot="description" class="text-muted">The tiered, volume, and stairstep pricing plans are best explained with an illustration.
                <a target="_blank" tabindex="-1"
                href="https://help.rebilly.com/invoices-and-subscriptions/choosing-a-pricing-formula">Click here to learn more.</a>
            </p>
            <div slot="content" class="tile">
                <plan-pricing
                    :currency-sign="plan.currency | currencySign"
                    :plan-pricing="plan.pricing"
                    :product-unit="productUnit"
                    :validate="$v.plan"
                    @input="updatePlanPricing"/>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Recurring Settings</h2>
            <div slot="content" class="tile">
                <div class="tile-content cursor-pointer">
                    <div class="grid-content grid-unstackable">
                        <div class="grid-item">
                            <v-checkbox
                                label="Has Recurring Settings"
                                :value="true"
                                v-model="isRecurringPlan"
                            />
                        </div>
                    </div>
                </div>
                <div v-if="isRecurringPlan">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <p class="stack-s">Recurs</p>
                                <v-radio
                                    class="stack-s"
                                    label="Forever (until canceled)"
                                    :model="recurringInterval"
                                    @update="updateBillingSchedule"
                                    value="forever"/>
                                <div class="grid-fitted grid-unstackable align-items-center stack-s">
                                    <v-radio
                                        class="grid-item no-flex inline-s"
                                        label="Limited to"
                                        :model="recurringInterval"
                                        @update="updateBillingSchedule"
                                        value="limited"/>
                                    <r-input
                                        v-if="recurringInterval !== 'limited'"
                                        class="grid-item no-flex inline-s"
                                        style="width: 70px;"
                                        :disabled="true"/>
                                    <r-input
                                        v-if="plan.isRecurringLimited"
                                        class="grid-item no-flex inline-s"
                                        style="width: 70px;"
                                        v-model="plan.recurringInterval.limit"
                                        :validate="$v.plan.recurringInterval.limit"/>
                                    <span class="grid-item">periods <span class="text-muted">(e.g. 3 times)</span></span>
                                </div>
                                <v-radio
                                    label="Never"
                                    :model="recurringInterval"
                                    @update="updateBillingSchedule"
                                    value="never"/>
                            </div>
                            <div v-if="plan.recurringInterval !== null"
                                 class="grid-item grid-item-half grid-item-break-half">
                                <p class="stack-xs">Recurring Period</p>
                                <div class="grid-content">
                                    <div class="grid-item">
                                        <r-input
                                            v-model="plan.recurringInterval.length"
                                            :validate="$v.plan.recurringInterval.length"/>
                                    </div>
                                    <div class="grid-item">
                                        <v-select
                                            :options="timeUnitList"
                                            :validate="$v.plan.recurringInterval.unit"
                                            v-model="plan.recurringInterval.unit"
                                            @input="updateRecurringIntervalUnit"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <template v-if="plan.recurringInterval !== null">
                        <div class="tile-title">
                            <h3>Billing Timing</h3>
                        </div>
                        <div class="tile-content">
                            <div class="grid-content">
                                <div class="grid-item">
                                    <v-radio
                                        v-for="billingTiming in billingTimingsOptionsList"
                                        class="stack-m"
                                        :key="`plan-recurringInterval-billingTiming-${billingTiming.value}`"
                                        :label="billingTiming.label"
                                        :value="billingTiming.value"
                                        @update="updateRecurringBillingTiming"
                                        :model="plan.recurringInterval.billingTiming"
                                        group="recurringIntervalBillingTimingGroup"/>
                                </div>
                            </div>
                        </div>
                    </template>
                    <!-- service anchor form -->
                    <template v-if="plan.recurringInterval !== null">
                        <div class="tile-title">
                            <h3>Service Period Anchor</h3>
                        </div>
                        <recurring-interval-form
                            description="Instruction for calculating the service period anchor. This is used in conjunction with the subscription start to calculate the time the service period starts and ends."
                            disabled-label="Not available on selected Period"
                            empty-label="Please select a Recurring Period"
                            checkbox-label="Has Service Period Anchor "
                            :model="PeriodAnchorInstructionModel"
                            :available-plan-periods="availablePlanPeriods"
                            :validate="$v.plan.recurringInterval.periodAnchorInstruction"
                            :instruction="periodAnchorInstruction"
                            @update="updatePeriodAnchorInstruction"/>
                    </template>
                    <div class="tile-title">
                        <h3>Invoice Time Shift</h3>
                    </div>
                    <invoice-time-shift-form
                        description="The calculation instruction of billing time. This is used in conjunction with the service period anchor to calculate the time the invoice is issued."
                        :model="InvoiceTimeShiftModel"
                        :validate="$v.plan.invoiceTimeShift"
                        :instruction="plan.invoiceTimeShift"
                        @update="updateInvoiceTimeShift"/>
                    <advanced-configuration
                        :plan-trial="plan.trial"
                        :plan-setup="plan.setup"
                        :currency-sign="plan.currency | currencySign"
                        :validate="$v.plan"
                        @input="updateAdvancedConfiguration"
                        @trial="updateHasTrial"
                        @setup="updateHasSetup"/>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link
                    v-if="$auth.$pages['products'].isAllowed"
                    class="btn inline-s" :to="{name: 'products'}">
                    Cancel
                </router-link>
                <r-button type="primary" @click="save" :loading="isLoading">Add Pricing Plan</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vErrorMessages,
        vFormTile,
        vIcon,
        vIconSprites,
        vRadio,
        vSelect,
        vToggle,
        vCheckbox,
    } from '@/components/form';
    import decimal from 'vuelidate/lib/validators/decimal';
    import {between, numeric, required} from 'vuelidate/lib/validators';
    import resourceId from '@/components/vuelidate/validators/resource-id';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import PlanModel from '@/common/models/plan-model';
    import InvoiceTimeShiftModel from '@/common/models/invoice-time-shift-model';
    import PeriodAnchorInstructionModel from '@/common/models/period-anchor-instruction-model';
    import _ from '@/common/helpers';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import PlanPricing from './components/plan-pricing';
    import AdvancedConfiguration from './components/advanced-configuration';
    import RecurringIntervalForm from '@/components/billing-schedule/recurring-interval-form';
    import InvoiceTimeShiftForm from '@/components/billing-schedule/invoice-time-shift-form';
    import getVuelidatePredicates from '@/common/helpers/get-vuelidate-predicates';

    export default {
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'plan.id',
                source: 'plan.name',
                entityCallback(id) {
                    return this.$api(({plans}) => plans.get({id}));
                },
            }),
        ],
        components: {
            vTitle,
            vIconSprites,
            vSelect,
            vFormTile,
            vToggle,
            vRadio,
            vIcon,
            vCheckbox,
            vErrorMessages,
            vInlineNotifications,
            PlanPricing,
            AdvancedConfiguration,
            vCustomFieldsManager,
            RecurringIntervalForm,
            InvoiceTimeShiftForm,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
            productId: {
                type: String,
            },
            productUnit: {
                type: String,
            },
        },
        data() {
            return {
                PeriodAnchorInstructionModel,
                InvoiceTimeShiftModel,
                plan: new PlanModel(),
                currenciesList: CURRENCIES_LIST,
                isRecurringPlan: false,
                hasTrial: false,
                hasSetup: false,
            };
        },
        validations() {
            const validation = {
                plan: {
                    id: {resourceId},
                    name: {required},
                    recurringInterval: {},
                },
            };
            if (this.isRecurringPlan && this.recurringInterval === PlanModel.Recurs.forever) {
                validation.plan.recurringInterval = {
                    unit: {required},
                    length: {required, decimal},
                };
            }
            if (this.isRecurringPlan && this.recurringInterval === PlanModel.Recurs.limited) {
                validation.plan.recurringInterval = {
                    unit: {required},
                    length: {required, decimal},
                    limit: {required, decimal},
                };
            }
            if (this.hasTrial) {
                validation.plan.trial = {
                    price: {required, decimal},
                    period: {
                        unit: {required},
                        length: {required, decimal},
                    },
                };
            }

            if (this.hasSetup) {
                validation.plan.setup = {
                    price: {required, decimal},
                };
            }

            if (this.plan.pricing.isSimple) {
                validation.plan.pricing = {
                    price: {required, decimal},
                };
            }
            if (this.plan.pricing.isBracket) {
                const bracketMaxQuantityMinValue = (previousBracket) => {
                    if (previousBracket) {
                        const {maxQuantity} = previousBracket;
                        return quantity => quantity > maxQuantity;
                    }
                    return () => true;
                };
                validation.plan.pricing = {
                    brackets: {
                        ...this.plan.pricing.brackets.map((bracket, index, list) => {
                            if (index === this.plan.pricing.brackets.length - 1) {
                                return {
                                    price: {required, decimal},
                                };
                            }
                            return {
                                price: {required, decimal},
                                maxQuantity: {
                                    validQuantity: bracketMaxQuantityMinValue(list[index - 1]),
                                    required,
                                    decimal,
                                },
                            };
                        }),
                    },
                };
            }

            validation.plan.recurringInterval.periodAnchorInstruction = this.getPeriodAnchorInstructionValidation();

            if (this.plan.invoiceTimeShift) {
                validation.plan.invoiceTimeShift = {};
                validation.plan.invoiceTimeShift.issueTimeShift = !this.plan.invoiceTimeShift.issueTimeShift
                    ? {}
                    : {
                        chronology: {
                            required,
                        },
                        duration: {
                            required,
                        },
                        unit: {
                            required,
                        },
                    };
                validation.plan.invoiceTimeShift.dueTimeShift = !this.plan.invoiceTimeShift.dueTimeShift
                    ? {}
                    : {
                        duration: {
                            required,
                        },
                        unit: {
                            required,
                        },
                    };
            }

            return validation;
        },
        computed: {
            recurringInterval() {
                if (this.plan.recurringInterval === null) {
                    return PlanModel.Recurs.never;
                }
                if (this.plan.recurringInterval.limit === null) {
                    return PlanModel.Recurs.forever;
                }
                return PlanModel.Recurs.limited;
            },
            timeUnitList() {
                return [
                    {label: this.timeUnitToPlural('day'), value: 'day'},
                    {label: this.timeUnitToPlural('week'), value: 'week'},
                    {label: this.timeUnitToPlural('month'), value: 'month'},
                    {label: this.timeUnitToPlural('year'), value: 'year'},
                ];
            },
            availablePlanPeriods() {
                if (this.plan.recurringInterval && this.plan.recurringInterval.unit) {
                    return [this.plan.recurringInterval.unit];
                }
                return [];
            },
            periodAnchorInstruction() {
                return this.plan.recurringInterval
                    ? this.plan.recurringInterval.periodAnchorInstruction
                    : null;
            },
            validationPredicates() {
                const items = getVuelidatePredicates(this.$v);
                return items.filter(
                    item => item.id.includes('periodAnchorInstruction') || item.id.includes('invoiceTimeShift'),
                ).map((item) => {
                    switch (item.rule) {
                        case 'plan.invoiceTimeShift.issueTimeShift.duration.required':
                            return {
                                ...item,
                                message: 'Invoice issue time shift duration is required',
                            };
                        default:
                            return item;
                    }
                });
            },
            billingTimingsOptionsList() {
                return Object.values(PlanModel.RecurringIntervalBillingTimings)
                    .map(value => ({value, label: _.capitalize(value)}));
            },
        },
        methods: {
            getPeriodAnchorInstructionValidation() {
                if (!this.plan.recurringInterval || !this.plan.recurringInterval.periodAnchorInstruction) {
                    return {};
                }
                const {periodAnchorInstruction} = this.plan.recurringInterval;
                const validations = {
                    method: {required},
                    day: {},
                    week: {},
                    time: {},
                };

                switch (periodAnchorInstruction.method) {
                    case PeriodAnchorInstructionModel.PeriodAnchorMethods.dayOfMonth:
                        Object.assign(validations, {
                            day: {
                                required,
                                numeric,
                                between: between(1, 31),
                            },
                            time: {},
                        });
                        break;
                    case PeriodAnchorInstructionModel.PeriodAnchorMethods.dayOfWeek:
                        Object.assign(validations, {
                            day: {
                                required,
                            },
                            time: {},
                        });
                        break;
                    default:
                        break;
                }
                return validations;
            },
            timeUnitToPlural(unit) {
                const {length} = this.plan.recurringInterval;
                return _.pluralize(unit, length === null ? length : +length);
            },
            updateBillingSchedule(value) {
                this.plan.setRecurringType(value);
            },
            updatePlanPricing({planPricing}) {
                this.plan.pricing = planPricing;
            },
            updateAdvancedConfiguration({trial, setup}) {
                this.plan.trial = trial;
                this.plan.setup = setup;
            },
            updateHasTrial(value) {
                if (this.plan.trial) {
                    this.hasTrial = true;
                }
                this.hasTrial = value;
            },
            updateHasSetup(value) {
                this.hasSetup = value;
            },
            updateInvoiceTimeShift(invoiceTimeShift) {
                this.plan.invoiceTimeShift = new InvoiceTimeShiftModel({...invoiceTimeShift});
            },
            updatePeriodAnchorInstruction(periodAnchorInstruction) {
                const recurringInterval = this.plan.recurringInterval ? {...this.plan.recurringInterval} : {};
                recurringInterval.periodAnchorInstruction = periodAnchorInstruction
                    ? new PeriodAnchorInstructionModel({...periodAnchorInstruction})
                    : null;
                this.plan.recurringInterval = recurringInterval;
            },
            updateRecurringIntervalUnit() {
                this.plan.recurringInterval.periodAnchorInstruction = null;
            },
            updateRecurringBillingTiming(value) {
                this.plan.recurringInterval.billingTiming = value;
            },
            async save() {
                this.$v.$touch();

                if (this.$v.plan.$error) {
                    this.scrollTop();
                    return;
                }

                this.isLoading = true;
                try {
                    if (!this.isRecurringPlan) {
                        this.plan.recurringInterval = null;
                    }
                    if (this.isRecurringPlan && this.recurringInterval === PlanModel.Recurs.never) {
                        this.plan.setRecurringType(PlanModel.Recurs.limited);
                        this.plan.recurringInterval = {
                            ...this.plan.recurringInterval,
                            length: 1,
                            unit: 'month',
                            limit: '1',
                        };
                    }
                    const params = {
                        id: this.plan.id,
                        data: {
                            ...this.plan.getPayload(),
                            productId: this.productId,
                        },
                    };
                    const plan = await this.$api(({plans}) => plans.update(params));
                    this.$toast.positive(`Plan successfully created`);
                    this.$router.push({name: 'product-details', params: {id: this.productId}});
                    this.triggerIntercomEvent({
                        name: 'Created a plan',
                        meta: {
                            name: plan.fields.name,
                        },
                    });
                } catch (error) {
                    this.$toast.fromError(error, `Plan could not be created`);
                    this.isLoading = false;
                    this.serverError = error;
                }
            },
        },
    };
</script>
